import { Dialog, DialogContent, MobileDialogContent, NestedDialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslatedToast } from "@/hooks/useTranslatedToast";
import { CheckCircle2, Target, MessageSquare, UserCheck, TrendingUp, Copy, Gift, FileCheck, Star, Sparkles, Share2, Loader2, X } from "lucide-react";
import { safeCopyToClipboard } from "@/utils/safeCopy";
import { BountyWorkflowSummary, WorkflowStageConfig } from "@/types/bounty-workflow";
import { HeadingMedium, BodyMedium, BodySmall } from "@/components/ui/typography";
import { useEffect, useState, useRef } from "react";
import { Separator } from "@/components/ui/separator";
import { useTranslation } from "@/hooks/useTranslation";
import { useSecureReferralToken } from "@/hooks/useSecureReferralToken";
import { supabase } from "@/integrations/supabase/client";

interface ReferralBountyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bountySummary: BountyWorkflowSummary | null;
  referralLink: string;
  jobTitle: string;
  loading?: boolean;
  nested?: boolean;
  jobId?: string;
  companyId?: string;
  autoGenerateLink?: boolean;
  onLinkGenerated?: (link: string, bounty: BountyWorkflowSummary | null) => void;
}

const STAGE_ICONS: Record<string, any> = {
  'resume_review': Target,
  'multiple_choice': CheckCircle2,
  'ai_interview': MessageSquare,
  'human_interview': UserCheck,
  'final_verdict': TrendingUp,
};

const formatCurrency = (cents: number, currency: string = 'USD') => {
  const amount = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);
};

const getStageTitle = (stage: WorkflowStageConfig): string => {
  if (stage.stage_name) return stage.stage_name;
  
  const titleMap: Record<string, string> = {
    'resume_review': 'Resume Review',
    'multiple_choice': 'Multiple Choice Quiz',
    'ai_interview': 'General Interview by Evee',
    'human_interview': 'Human Interview',
    'final_verdict': 'Final Decision',
  };
  
  return titleMap[stage.stage_id] || stage.stage_id.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
};

const getRequirementText = (stage: WorkflowStageConfig, t: (key: string, params?: any) => string): string => {
  if (!stage.qualification_requirements?.enabled) {
    return t('referral_bounty.requirements.candidate_completes');
  }
  
  const req = stage.qualification_requirements;
  
  if (req.minimum_score !== undefined) {
    return t('referral_bounty.requirements.candidate_scores', { score: req.minimum_score });
  }
  
  if (req.minimum_hiring_decision) {
    return req.minimum_hiring_decision === 'hire' 
      ? t('referral_bounty.requirements.candidate_hired')
      : t('referral_bounty.requirements.candidate_talent_reserve');
  }
  
  return t('referral_bounty.requirements.candidate_completes');
};

export function ReferralBountyDialog({
  open,
  onOpenChange,
  bountySummary,
  referralLink,
  jobTitle,
  loading = false,
  nested = false,
  jobId,
  companyId,
  autoGenerateLink = false,
  onLinkGenerated,
}: ReferralBountyDialogProps) {
  const { t } = useTranslation('ui');
  const toast = useTranslatedToast();
  const { generateToken } = useSecureReferralToken();
  
  const [internalLink, setInternalLink] = useState(referralLink);
  const [isLinkGenerating, setIsLinkGenerating] = useState(false);
  const [generationError, setGenerationError] = useState(false);
  const [internalBounty, setInternalBounty] = useState<BountyWorkflowSummary | null>(null);
  
  // Ref to prevent concurrent generation calls
  const generatingRef = useRef(false);

  // Reset states when dialog opens fresh
  useEffect(() => {
    if (open && autoGenerateLink) {
      setInternalLink('');
      setIsLinkGenerating(false);
      setGenerationError(false);
      setInternalBounty(null);
      generatingRef.current = false;
    }
  }, [open, autoGenerateLink]);

  // Auto-generate link when dialog opens (ONLY TRIGGER POINT FOR TOKEN GENERATION)
  useEffect(() => {
    if (!open || !autoGenerateLink || !jobId || internalLink) return;
    
    // Prevent concurrent generation calls - this is the ONLY place where generation happens
    if (generatingRef.current) {
      console.log('⏭️ Generation already in progress, skipping duplicate call');
      return;
    }
    
    const generateLink = async () => {
      generatingRef.current = true;
      setIsLinkGenerating(true);
      setGenerationError(false);
      
      try {
        const result = await generateToken({ 
          jobId,
          companyId 
        });
        
        if (result) {
          setInternalLink(result.referral_url);
          
          // Fetch bounty information
          try {
            const { data: bountySettings } = await supabase
              .from('company_job_bounty_workflow_settings')
              .select('*')
              .eq('job_id', jobId as any)
              .single();
            
            if (bountySettings) {
              // Build bounty summary from fetched settings
              const stageConfig = (bountySettings as any).stage_bounty_config as any;
              const stages = stageConfig?.stages || [];
              const enabledStages = stages.filter((s: any) => s.bounty_enabled && s.bounty_amount > 0);
              
              const summary: BountyWorkflowSummary = {
                enabled: (bountySettings as any).bounty_enabled || false,
                settings: bountySettings as any,
                totalPotential: enabledStages.reduce((sum: number, s: any) => sum + s.bounty_amount, 0),
                totalAwarded: 0,
                totalPaid: 0,
                stageBreakdown: {}
              };
              
              setInternalBounty(summary);
              
              if (onLinkGenerated) {
                onLinkGenerated(result.referral_url, summary);
              }
            } else {
              // No bounty configured - points mode
              setInternalBounty({ 
                enabled: false, 
                totalPotential: 0, 
                totalAwarded: 0, 
                totalPaid: 0, 
                stageBreakdown: {} 
              });
              
              if (onLinkGenerated) {
                onLinkGenerated(result.referral_url, null);
              }
            }
          } catch (error) {
            console.error('Error fetching bounty data:', error);
            // Default to points mode on error
            setInternalBounty({ 
              enabled: false, 
              totalPotential: 0, 
              totalAwarded: 0, 
              totalPaid: 0, 
              stageBreakdown: {} 
            });
            
            if (onLinkGenerated) {
              onLinkGenerated(result.referral_url, null);
            }
          }
        } else {
          setGenerationError(true);
        }
      } catch (error) {
        console.error('Error generating link:', error);
        setGenerationError(true);
      } finally {
        setIsLinkGenerating(false);
        generatingRef.current = false;
      }
    };
    
    generateLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Note: Intentionally excluding generateToken and onLinkGenerated from dependencies
    // to prevent re-triggering on parent component re-renders. The generation should
    // only happen once when the dialog opens with autoGenerateLink enabled.
  }, [open, autoGenerateLink, jobId, companyId, internalLink]);

  // Update internal link when prop changes
  useEffect(() => {
    if (referralLink) {
      setInternalLink(referralLink);
    }
  }, [referralLink]);

  const displayLink = internalLink || referralLink;

  const handleCopy = async () => {
    const success = await safeCopyToClipboard(displayLink);
    
    if (success) {
      toast.success(t('referral_bounty.link_copied'));
    } else {
      // Select the input for manual copy
      const input = document.querySelector('input[readonly]') as HTMLInputElement;
      if (input) {
        input.focus();
        input.select();
      }
      toast.info(t('referral_bounty.select_and_copy'));
    }
  };

  const handleShare = async () => {
    if (!navigator.share) return;
    
    try {
      await navigator.share({
        title: jobTitle,
        text: t('referral_bounty.share_text', { jobTitle }),
        url: displayLink
      });
    } catch (err) {
      // User cancelled or share failed
      console.log('Share cancelled or failed:', err);
    }
  };

  const handleRetry = () => {
    setInternalLink('');
    setGenerationError(false);
    generatingRef.current = false;
  };

  // Merge prop bounty with internally fetched bounty
  const effectiveBounty = bountySummary || internalBounty;
  
  const enabledStages = effectiveBounty?.settings?.stage_bounty_config?.stages?.filter(
    stage => stage.bounty_enabled && stage.bounty_amount > 0
  ) || [];

  const totalPotential = enabledStages.reduce((sum, stage) => sum + stage.bounty_amount, 0);
  const currency = effectiveBounty?.settings?.currency || 'USD';
  
  // Check if this is points mode (no bounty enabled or null)
  const isPointsMode = effectiveBounty === null || !effectiveBounty.enabled;

  const ContentComponent = nested ? NestedDialogContent : MobileDialogContent;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <ContentComponent className="p-0 sm:max-w-[520px] flex flex-col overflow-hidden">
        <div className="px-4 sm:px-6 py-4 flex-shrink-0">
          <DialogHeader>
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1 min-w-0">
                <DialogTitle className="flex items-center gap-2">
                  {isPointsMode ? (
                    <>
                      <Gift className="w-5 h-5" />
                      {t('referral_bounty.points_title')}
                    </>
                  ) : (
                    <>
                      <TrendingUp className="w-5 h-5 text-primary" />
                      {t('referral_bounty.bounty_title')}
                    </>
                  )}
                </DialogTitle>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 flex-shrink-0"
                onClick={() => onOpenChange(false)}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </Button>
            </div>
            <DialogDescription>
              {isPointsMode 
                ? t('referral_bounty.points_description')
                : t('referral_bounty.bounty_description', { jobTitle })}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 sm:px-6 pb-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="space-y-6">
          {loading ? (
            <div className="space-y-4">
              <div className="h-20 bg-muted animate-pulse rounded-lg" />
              <div className="h-32 bg-muted animate-pulse rounded-lg" />
            </div>
          ) : (
            <>
              {/* Referral Link Display Section */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <BodySmall className="font-medium">
                  {t('referral_bounty.your_referral_link')}
                </BodySmall>
                
                {isLinkGenerating ? (
                  <div className="flex items-center justify-center py-6">
                    <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-sm text-muted-foreground">
                      {t('referral_bounty.generating_link')}
                    </span>
                  </div>
                ) : generationError ? (
                  <div className="text-center py-4 space-y-3">
                    <p className="text-sm text-destructive">
                      {t('referral_bounty.generation_failed')}
                    </p>
                    <Button
                      variant="outline"
                      onClick={handleRetry}
                      className="min-h-[44px]"
                    >
                      {t('referral_bounty.retry')}
                    </Button>
                  </div>
                ) : displayLink ? (
                  <>
                    {/* Read-only input showing the link */}
                    <input
                      type="text"
                      readOnly
                      value={displayLink}
                      onClick={(e) => e.currentTarget.select()}
                      className="w-full px-3 py-2 bg-background border rounded-md text-sm font-mono"
                    />
                    
                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <Button
                        variant="default"
                        className="flex-1 min-h-[44px]"
                        onClick={handleCopy}
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        {t('referral_bounty.copy_link')}
                      </Button>
                      
                      {navigator.share && (
                        <Button
                          variant="outline"
                          className="flex-1 min-h-[44px]"
                          onClick={handleShare}
                        >
                          <Share2 className="w-4 h-4 mr-2" />
                          {t('referral_bounty.share')}
                        </Button>
                      )}
                    </div>
                  </>
                ) : null}
              </div>

              <Separator className="my-4" />

              {isPointsMode ? (
                // Points Mode - Consistent with Bounty Design
                <>
                  {/* Job Reference */}
                  <div className="mb-6">
                    <BodySmall className="text-muted-foreground mb-1">{t('referral_bounty.referring_for')}</BodySmall>
                    <BodyMedium className="font-medium">{jobTitle}</BodyMedium>
                  </div>

                  {/* Maximum Points Highlight */}
                  <div className="border rounded-lg p-6 bg-card">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <BodySmall className="text-muted-foreground mb-1">
                          {t('referral_bounty.maximum_possible_points')}
                        </BodySmall>
                        <HeadingMedium>
                          {t('referral_bounty.points_suffix_plural', { points: 126 })}
                        </HeadingMedium>
                        <BodySmall className="text-muted-foreground mt-1">
                          {t('referral_bounty.per_successful_hire')}
                        </BodySmall>
                      </div>
                      <div className="bg-muted p-3 rounded-full flex-shrink-0">
                        <Sparkles className="w-6 h-6 text-muted-foreground" />
                      </div>
                    </div>
                  </div>

                  {/* How to Earn Points */}
                  <div>
                    <h3 className="text-sm font-medium mb-3">{t('referral_bounty.how_to_earn_points')}</h3>
                    <div className="space-y-3">
                      {/* Application Points */}
                      <div className="rounded-lg border bg-card p-4">
                        <div className="flex items-start gap-3">
                          <div className="bg-muted p-2 rounded-full flex-shrink-0">
                            <FileCheck className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <BodyMedium className="font-medium">
                                1. {t('referral_bounty.successful_application')}
                              </BodyMedium>
                              <Badge variant="secondary" className="flex-shrink-0">
                                {t('referral_bounty.points_suffix', { points: 1 })}
                              </Badge>
                            </div>
                            <BodySmall className="text-muted-foreground">
                              {t('referral_bounty.requirements.candidate_applies')}
                            </BodySmall>
                          </div>
                        </div>
                      </div>

                      {/* Interview Points */}
                      <div className="rounded-lg border bg-card p-4">
                        <div className="flex items-start gap-3">
                          <div className="bg-muted p-2 rounded-full flex-shrink-0">
                            <Target className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <BodyMedium className="font-medium">
                                2. {t('referral_bounty.successful_interview')}
                              </BodyMedium>
                              <Badge variant="secondary" className="flex-shrink-0">
                                {t('referral_bounty.points_suffix_plural', { points: 25 })}
                              </Badge>
                            </div>
                            <BodySmall className="text-muted-foreground">
                              {t('referral_bounty.requirements.candidate_scores', { score: 60 })}
                            </BodySmall>
                          </div>
                        </div>
                      </div>

                      {/* Hire Points */}
                      <div className="rounded-lg border bg-card p-4">
                        <div className="flex items-start gap-3">
                          <div className="bg-muted p-2 rounded-full flex-shrink-0">
                            <Star className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <BodyMedium className="font-medium">
                                3. {t('referral_bounty.successful_hire')}
                              </BodyMedium>
                              <Badge variant="secondary" className="flex-shrink-0">
                                {t('referral_bounty.points_suffix_plural', { points: 100 })}
                              </Badge>
                            </div>
                            <BodySmall className="text-muted-foreground">
                              {t('referral_bounty.requirements.candidate_hired')}
                            </BodySmall>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : enabledStages.length === 0 ? (
            <div className="text-center py-8">
              <BodyMedium className="text-muted-foreground">
                {t('referral_bounty.no_bounty_active')}
              </BodyMedium>
            </div>
          ) : (
            <>
              {/* Job Title */}
              <div className="mb-6">
                <BodySmall className="text-muted-foreground mb-1">{t('referral_bounty.referring_for')}</BodySmall>
                <BodyMedium className="font-medium">{jobTitle}</BodyMedium>
              </div>

              {/* Maximum Possible Earnings */}
              {(() => {
                const hasLimits = enabledStages.some(stage => stage.max_limit);
                const maxPossible = hasLimits 
                  ? enabledStages.reduce((sum, stage) => {
                      return sum + (stage.bounty_amount * (stage.max_limit || 0));
                    }, 0)
                  : null;

                return (
                  <div className="border rounded-lg p-6 bg-card">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <BodySmall className="text-muted-foreground mb-1">
                          {t('referral_bounty.maximum_possible_earnings')}
                        </BodySmall>
                        <HeadingMedium>
                          {maxPossible ? formatCurrency(maxPossible, currency) : t('referral_bounty.unlimited')}
                        </HeadingMedium>
                        {!maxPossible && (
                          <BodySmall className="text-muted-foreground mt-1">
                            {t('referral_bounty.unlimited_subtitle', { amount: formatCurrency(totalPotential, currency) })}
                          </BodySmall>
                        )}
                      </div>
                      <div className="bg-muted p-3 rounded-full flex-shrink-0">
                        <Sparkles className="w-6 h-6 text-muted-foreground" />
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* How to Earn Bounty */}
              <div>
                <h3 className="text-sm font-medium mb-3">{t('referral_bounty.how_to_earn_bounty')}</h3>

                <div className="space-y-3">
                  {enabledStages.map((stage, index) => {
                    const StageIcon = STAGE_ICONS[stage.stage_id] || CheckCircle2;
                    
                    return (
                      <div
                        key={stage.stage_id}
                        className="rounded-lg border bg-card p-4"
                      >
                        <div className="flex items-start gap-3">
                          <div className="bg-muted p-2 rounded-full flex-shrink-0">
                            <StageIcon className="w-4 h-4" />
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <BodyMedium className="font-medium">
                                {index + 1}. {getStageTitle(stage)}
                              </BodyMedium>
                              <Badge variant="secondary" className="flex-shrink-0">
                                {formatCurrency(stage.bounty_amount, currency)}
                              </Badge>
                            </div>
                            
                            <BodySmall className="text-muted-foreground">
                              {getRequirementText(stage, t)}
                            </BodySmall>
                            
                            {stage.max_limit && (
                              <BodySmall className="text-muted-foreground mt-1">
                                {t('referral_bounty.limited_to_referrals', { limit: stage.max_limit })}
                              </BodySmall>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
            </>
          )}
          </div>
        </div>
      </ContentComponent>
    </Dialog>
  );
}